/**
 * Image-notice review preview aligned with the native app feed card
 * (`BaseNoticeItem` + `InfoNoticeItem`): header row (logo + school + date),
 * title + optional body, then a square full-width hero image below.
 */
import React from "react";
import {
  Avatar,
  Box,
  Typography,
  Paper,
  useTheme,
} from "@mui/material";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const AVATAR_BOX = 28;
const LOGO_GAP = 8;

const formatNativeStyleDate = (d: Date) => {
  const datePart = d.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${datePart} ${timePart}`;
};

const truncate = (text: string, max: number) => {
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
};

export interface ImageNoticeCardPreviewProps {
  title: string;
  body: string;
  imageUrl?: string | null;
  /** Live generated artwork uses the same feed-card frame as uploaded images. */
  hero?: React.ReactNode;
  /** When true, hero uses a video element instead of an image. */
  heroIsVideo?: boolean;
  createdAt: Date;
  /** Primary header line (school name, or subcircle name when posting to a subcircle). */
  schoolName: string;
  schoolLogo?: string | null;
  /** Second header line for subcircle posts — main organization name on native. */
  organizationName?: string | null;
  isUrgent: boolean;
}

const ImageNoticeCardPreview: React.FC<ImageNoticeCardPreviewProps> = ({
  title,
  body,
  imageUrl,
  hero,
  heroIsVideo = false,
  createdAt,
  schoolName,
  schoolLogo,
  organizationName,
  isUrgent,
}) => {
  const theme = useTheme();
  const trimmedUrl = imageUrl?.trim();
  const hasUrl = Boolean(trimmedUrl);
  const headerPrimary = truncate(schoolName || "", 22);
  const dateLabel = formatNativeStyleDate(createdAt);
  const bodyTrim = (body || "").trim();
  const hasDescription = bodyTrim.length > 0;

  return (
    <Box sx={{ mb: 0 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 400,
          display: "flex",
          flexDirection: "column",
          borderRadius: 0,
          overflow: "hidden",
          border: 0,
          borderColor: theme.palette.borders?.slate ?? theme.palette.divider,
          bgcolor: theme.palette.background.paper,
          mx: "auto",
        }}
      >
        {/* Top: logo + header (native BaseNoticeItem top row) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            pt: 1,
            pb: 0.5,
            px: 1.5,
            gap: `${LOGO_GAP}px`,
          }}
        >
          <Box
            sx={{
              width: AVATAR_BOX,
              height: AVATAR_BOX,
              borderRadius: "50%",
              overflow: "hidden",
              flexShrink: 0,
              mt: 0,
              bgcolor: theme.palette.grey[200],
            }}
          >
            {schoolLogo ? (
              <Avatar
                src={schoolLogo}
                alt=""
                sx={{
                  width: AVATAR_BOX,
                  height: AVATAR_BOX,
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: AVATAR_BOX,
                  height: AVATAR_BOX,
                  fontSize: "0.95rem",
                  bgcolor: theme.palette.primary.main,
                }}
              >
                {(schoolName || "?").charAt(0).toUpperCase()}
              </Avatar>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: "text.primary",
                  lineHeight: 1.2,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {headerPrimary}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 9.5,
                  fontWeight: 600,
                  color: "text.primary",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {dateLabel}
              </Typography>
            </Box>
            {organizationName ? (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontSize: 10,
                  color: "text.secondary",
                  mt: 0.25,
                  lineHeight: 1.2,
                }}
              >
                {organizationName}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {/* App feed copy sits at the content inset, independently of the avatar. */}
        <Box
          sx={{
            px: 2,
            pb: 1,
            pt: 0.25,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 0.75,
            }}
          >
            <Typography
              component="div"
              sx={{
                flex: 1,
                fontSize: 14,
                fontWeight: 700,
                lineHeight: "18px",
                color: "text.primary",
                mt: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {title || "Untitled"}
            </Typography>
            <Box aria-hidden="true" sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "primary.main", flexShrink: 0, mt: .5 }} />
            {isUrgent && (
              <WarningAmberIcon
                sx={{
                  fontSize: 18,
                  color: theme.palette.warning?.main ?? "#fbbf24",
                  mt: 0.75,
                  flexShrink: 0,
                }}
                aria-label="Urgent"
              />
            )}
          </Box>
          {hasDescription ? (
            <Typography
              variant="body2"
              sx={{
                fontSize: 14,
                color: "text.primary",
                lineHeight: "18px",
                mt: 0.75,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {bodyTrim}
            </Typography>
          ) : null}
        </Box>

        {/* Square hero image — native InfoNoticeItem imageContainer aspectRatio 1, cover */}
        <Box
          sx={{
            width: "100%",
            aspectRatio: "1 / 1",
            bgcolor: theme.palette.grey[100],
            position: "relative",
            overflow: "hidden",
            alignSelf: "stretch",
            mt: 0,
          }}
        >
          {hero ?? (hasUrl ? (
            heroIsVideo ? (
              <Box
                component="video"
                src={trimmedUrl}
                controls
                playsInline
                preload="metadata"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  bgcolor: "common.black",
                }}
              />
            ) : (
              <Box
                component="img"
                src={trimmedUrl}
                alt={title ? `${title}` : "Notice image"}
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "text.secondary",
              }}
            >
              <ImageOutlinedIcon sx={{ fontSize: 42, opacity: 0.5 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {heroIsVideo ? "Video will appear here" : "Photo will appear here"}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default ImageNoticeCardPreview;
